import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, Users, ShoppingBag, UserPlus, ChevronRight, Trophy, Target, Facebook, Instagram, MessageCircle, Share2, ArrowRight } from 'lucide-react';
import axios from 'axios';

const Home = () => {
  const [fixtures, setFixtures] = useState([]);
  const [news, setNews] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  const clubLogo = "https://customer-assets.emergentagent.com/job_cafc-platform/artifacts/d9rcitex_OurLogo.png";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [fixturesRes, newsRes, sponsorsRes] = await Promise.all([
        axios.get('/fixtures'),
        axios.get('/news'),
        axios.get('/sponsors')
      ]);
      
      setFixtures(fixturesRes.data.slice(0, 3));
      setNews(newsRes.data.slice(0, 3));
      setSponsors(sponsorsRes.data.slice(0, 6));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const upcomingFixtures = fixtures.filter(f => f.status === 'upcoming').slice(0, 2);
  const recentResults = fixtures.filter(f => f.status === 'completed').slice(0, 2);

  return (
    <div className="fade-in">
      {/* Hero Section */}
      <section className="hero relative flex items-center justify-center text-center">
        <div className="hero-content max-w-4xl mx-auto px-4 z-10">
          <img 
            src={clubLogo} 
            alt="Crawley Athletic FC" 
            className="h-24 w-24 mx-auto mb-6 filter drop-shadow-lg"
          />
          <h1 className="hero-title text-white mb-6">
            Welcome to Crawley Athletic Football Club
          </h1>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Crawley Athletic FC offers open-access football for players of all abilities and builds a welcoming, inclusive community for them and their families.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/fixtures" className="btn btn-white">
              <Calendar className="h-5 w-5" />
              View Fixtures
            </Link>
            <Link to="/shop" className="btn btn-secondary border-white text-white hover:bg-white hover:text-black">
              <ShoppingBag className="h-5 w-5" />
              Shop Now
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Link to="/squad" className="card text-center hover:scale-105 transition-transform">
              <Users className="h-12 w-12 mx-auto mb-4 text-black" />
              <h3 className="text-lg font-semibold mb-2">Squad & Staff</h3>
              <p className="text-gray-600 text-sm">Meet our players and coaching team</p>
            </Link>
            
            <Link to="/fixtures" className="card text-center hover:scale-105 transition-transform">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-black" />
              <h3 className="text-lg font-semibold mb-2">Fixtures & Results</h3>
              <p className="text-gray-600 text-sm">Latest matches and upcoming games</p>
            </Link>
            
            <Link to="/shop" className="card text-center hover:scale-105 transition-transform">
              <ShoppingBag className="h-12 w-12 mx-auto mb-4 text-black" />
              <h3 className="text-lg font-semibold mb-2">Club Shop</h3>
              <p className="text-gray-600 text-sm">Official merchandise and kit</p>
            </Link>
            
            <Link to="/join-us" className="card text-center hover:scale-105 transition-transform">
              <UserPlus className="h-12 w-12 mx-auto mb-4 text-black" />
              <h3 className="text-lg font-semibold mb-2">Join Us</h3>
              <p className="text-gray-600 text-sm">Become part of the Crawley family</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Fixtures & Results */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Upcoming Fixtures */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold">Upcoming Fixtures</h2>
                <Link to="/fixtures" className="text-black hover:underline flex items-center">
                  View All <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
              
              {loading ? (
                <div className="card">
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </div>
                </div>
              ) : upcomingFixtures.length > 0 ? (
                <div className="space-y-4">
                  {upcomingFixtures.map((fixture) => (
                    <div key={fixture.id} className="card">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="fixture-date">
                            {new Date(fixture.match_date).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short'
                            })}
                          </div>
                          <div>
                            <h3 className="font-semibold">
                              {fixture.is_home ? 'Crawley Athletic' : fixture.away_team} vs{' '}
                              {fixture.is_home ? fixture.away_team : 'Crawley Athletic'}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {fixture.match_time} • {fixture.venue}
                            </p>
                          </div>
                        </div>
                        <Trophy className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card text-center">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600">No upcoming fixtures scheduled</p>
                </div>
              )}
            </div>

            {/* Recent Results */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold">Recent Results</h2>
                <Link to="/fixtures" className="text-black hover:underline flex items-center">
                  View All <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
              
              {loading ? (
                <div className="card">
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </div>
                </div>
              ) : recentResults.length > 0 ? (
                <div className="space-y-4">
                  {recentResults.map((fixture) => (
                    <div key={fixture.id} className="card">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="fixture-date">
                            {new Date(fixture.match_date).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short'
                            })}
                          </div>
                          <div>
                            <h3 className="font-semibold">
                              {fixture.is_home ? 'Crawley Athletic' : fixture.away_team} {fixture.home_score}-{fixture.away_score}{' '}
                              {fixture.is_home ? fixture.away_team : 'Crawley Athletic'}
                            </h3>
                            <p className="text-sm text-gray-600">{fixture.venue}</p>
                          </div>
                        </div>
                        <Target className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card text-center">
                  <Target className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600">No recent results available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Club News */}
      {news.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Latest News</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {news.map((article) => (
                <NewsCard key={article.id} article={article} />
              ))}
            </div>
            <div className="text-center mt-12">
              <Link
                to="/news"
                className="inline-flex items-center bg-black text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition-colors font-semibold"
              >
                View All News
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Sponsors */}
      {sponsors.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Our Partners</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {sponsors.map((sponsor) => (
                <div key={sponsor.id} className="sponsor-card">
                  <img 
                    src={sponsor.logo_url} 
                    alt={sponsor.name}
                    className="sponsor-logo"
                  />
                  <h4 className="text-sm font-medium text-center">{sponsor.name}</h4>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/sponsors" className="btn btn-secondary">
                View All Partners
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Call to Action */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">Join the Crawley Athletic Family</h2>
          <p className="text-xl text-gray-200 mb-8">
            Whether you want to play, volunteer, or support the club, there's a place for you here.
          </p>
          <Link to="/join-us" className="btn btn-white">
            <UserPlus className="h-5 w-5" />
            Get Involved Today
          </Link>
        </div>
      </section>
    </div>
  );
};

// NewsCard component for homepage
const NewsCard = ({ article }) => {
  const navigate = useNavigate();

  const handleShare = (e, platform) => {
    e.stopPropagation(); // Prevent navigation when clicking share buttons
    
    const shareUrl = window.location.origin + `/news/${article.id}`;
    const shareText = encodeURIComponent(article.title + ' - Crawley Athletic FC');

    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      instagram: `https://www.instagram.com/`,
      whatsapp: `https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}`
    };

    if (platform === 'instagram') {
      const textToCopy = `${article.title} - ${shareUrl}`;
      
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(textToCopy).then(() => {
          showInstagramMessage();
        }).catch(() => {
          fallbackCopyToClipboard(textToCopy);
        });
      } else {
        // Fallback for older browsers or non-HTTPS
        fallbackCopyToClipboard(textToCopy);
      }
      return;
    }

    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      showInstagramMessage();
    } catch (err) {
      console.error('Failed to copy text: ', err);
      showInstagramError();
    }
    
    document.body.removeChild(textArea);
  };

  const showInstagramMessage = () => {
    const notification = document.createElement('div');
    notification.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%);
        color: white;
        padding: 16px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        max-width: 350px;
        animation: slideIn 0.3s ease-out;
      ">
        <div style="display: flex; align-items: center; gap: 10px;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
          <div>
            <div style="font-weight: 600; margin-bottom: 4px;">Link copied!</div>
            <div style="font-size: 12px; opacity: 0.9;">Open Instagram and paste the link in your story or post</div>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (document.body.contains(notification)) {
        notification.style.animation = 'slideOut 0.3s ease-in';
        setTimeout(() => document.body.removeChild(notification), 300);
      }
    }, 4000);
  };

  const showInstagramError = () => {
    const notification = document.createElement('div');
    notification.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ef4444;
        color: white;
        padding: 16px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        max-width: 350px;
      ">
        <div style="font-weight: 600;">Unable to copy link</div>
        <div style="font-size: 12px; margin-top: 4px;">Please copy the URL manually from your browser</div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 3000);
  };

  return (
    <div 
      className="card cursor-pointer hover:shadow-lg transition-shadow"
      onClick={() => navigate(`/news/${article.id}`)}
    >
      {article.image_url && (
        <img 
          src={article.image_url} 
          alt={article.title}
          className="w-full h-48 object-cover rounded-lg mb-4"
        />
      )}
      
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-xl font-semibold flex-1 mr-2">{article.title}</h3>
        
        {/* Share Buttons */}
        <div className="flex items-center space-x-1 opacity-70 hover:opacity-100 transition-opacity">
          <Share2 className="h-3 w-3 text-gray-400" />
          <button
            onClick={(e) => handleShare(e, 'facebook')}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
            title="Share on Facebook"
          >
            <Facebook className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => handleShare(e, 'instagram')}
            className="p-1 text-pink-600 hover:bg-pink-50 rounded"
            title="Copy for Instagram"
          >
            <Instagram className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => handleShare(e, 'whatsapp')}
            className="p-1 text-green-600 hover:bg-green-50 rounded"
            title="Share on WhatsApp"
          >
            <MessageCircle className="h-3 w-3" />
          </button>
        </div>
      </div>
      
      <p className="text-gray-600 mb-4 line-clamp-3">
        {article.summary || article.content.substring(0, 150) + '...'}
      </p>
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {new Date(article.created_at).toLocaleDateString('en-GB')}
        </p>
        <span className="text-black font-semibold text-sm hover:underline">
          Read More →
        </span>
      </div>
    </div>
  );
};

export default Home;